import type { ReactNode } from 'react'
import { createContext, useContext, useMemo } from 'react'

import { useBoolean } from 'usehooks-ts'

import {
  isZeroQuotePlaceholder,
  useFlag,
} from '@b-stock/fe-core/bstock-next/helpers'
import type {
  OrchestratedAuctionListingDetails,
  OrchestratedQuote,
} from '@b-stock/fe-core/bstock-next/queries'
import type { ActiveServiceDto } from '@b-stock/subscription-api-client'

import useAccountId from '@/buyer-portal/helpers/useAccountId'
import { FeatureFlags } from '@/helpers/featureFlags'

import { getContractAutoPayId, isContractListing } from '../../utils'
import { useAutoPayMethodContext } from '../AutoPayMethodProvider'
import BuyNowConfirmationModal from './BuyNowConfirmationModal'
import { useOnBuyNowConfirm } from './useOnBuyNowConfirm'

type BuyNowConfirmationModalContextValue = {
  openModal: () => void
  closeModal: () => void
  showModal: boolean
}

const BuyNowConfirmationModalContext =
  createContext<BuyNowConfirmationModalContextValue>({
    showModal: false,
    openModal: () => null,
    closeModal: () => null,
  })

export const useBuyNowConfirmationModalContext = () =>
  useContext(BuyNowConfirmationModalContext)

export const BuyNowConfirmationModalProvider = ({
  children,
}: {
  children: ReactNode
}) => {
  const {
    setFalse: closeModal,
    setTrue: openModal,
    value: showModal,
  } = useBoolean(false)

  const value = useMemo(
    () => ({ openModal, closeModal, showModal }),
    [closeModal, openModal, showModal]
  )

  return (
    <BuyNowConfirmationModalContext.Provider value={value}>
      {children}
    </BuyNowConfirmationModalContext.Provider>
  )
}

export const useBuyNowConfirmationModal = ({
  currency,
  listing,
  selectedQuote,
  sellerSubscription,
}: {
  currency?: string
  listing: OrchestratedAuctionListingDetails
  selectedQuote?: OrchestratedQuote | null
  sellerSubscription?: ActiveServiceDto | null
}) => {
  const { openModal, closeModal, showModal } = useContext(
    BuyNowConfirmationModalContext
  )

  const autoPayEnforced = useFlag(FeatureFlags.AUTOPAY_ENFORCED)
  const { selectedPaymentMethodId, isWireSelected } = useAutoPayMethodContext()
  const accountId = useAccountId()

  const isShippingQuoteAvailable =
    !!selectedQuote && !isZeroQuotePlaceholder(selectedQuote)
  const isContract = isContractListing(listing)
  const contractAutoPayId = getContractAutoPayId(listing)
  const contractAutoPayEnabled = useFlag(FeatureFlags.ENABLE_CONTRACT_AUTOPAY)
  const autoPayEnabledForListing =
    autoPayEnforced &&
    (!isContract || (contractAutoPayEnabled && !!contractAutoPayId))

  const onBuyNowConfirm = useOnBuyNowConfirm({
    closeModal,
    listing,
    selectedQuote,
    autoPayMethodId:
      autoPayEnabledForListing &&
      !listing.sellerCollects &&
      isShippingQuoteAvailable
        ? selectedPaymentMethodId
        : null,
    isWireSelected:
      autoPayEnabledForListing && !listing.sellerCollects
        ? isWireSelected
        : false,
  })

  const buyNowConfirmationModal = showModal ? (
    <BuyNowConfirmationModal
      buyerAccountId={accountId ?? ''}
      closeModal={closeModal}
      currency={currency}
      listing={listing}
      onBuyNowConfirm={onBuyNowConfirm}
      selectedQuote={selectedQuote}
      sellerSubscription={sellerSubscription}
    />
  ) : null

  return {
    buyNowConfirmationModal,
    onBuyNowConfirm,
    openModal,
    closeModal,
    showModal,
  }
}
